import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BsFillEyeFill, BsFillEyeSlashFill, BsPersonFill } from 'react-icons/bs';
import { MdEmail } from 'react-icons/md';
import { RiLockPasswordFill } from 'react-icons/ri';
import { validateEmail, validatePwd, validName } from '../../assets/Helper/utils';
import { postWithoutToken, setLocalStorage } from '../../API/Api';
import { PATHS, SESSION_PATHS } from '../../routes/paths';
import ReCAPTCHA from 'react-google-recaptcha';
import LOGO from '../../assets/images/comniqAI/comniq-logo-dark.svg';
import MOCKUP from '../../assets/images/comniqAI/mockup.svg';
import AuthLayout from './AuthLayout';
import ButtonLoader from '../../components/ButtonLoader';
import { useGoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../config/authConfig';
import { InteractionStatus } from '@azure/msal-browser';
import { jwtDecode } from 'jwt-decode';
import showToast from '../../components/Toast/toastHelper';
import { getIpAddress } from '../../utils/ipAddress';

const ValidationErrors = {
  empty: {
    first_name: 'First name Required',
    last_name: 'Last name Required',
    email: 'Email id Required',
    password: 'Password Required',
  },
  invalid: {
    first_name: 'First name must contain only letters and be max 20 characters',
    last_name: 'Last name must contain only letters and be max 20 characters',
    email: 'Invalid Email',
    password: 'Password must be 8–10 characters, with 1 uppercase, 1 lowercase, 1 number, and 1 special character (@#$/_-!&*).',
  },
};

const RegisterForm = () => {
  const recaptchaRef = useRef(null);
  const { instance, inProgress } = useMsal();
  const [captchaValue, setCaptchaValue] = useState(null);
  const [showPwd, setShowPwd] = useState(false);
  const [loader, setLoader] = useState(false);
  const [values, setValues] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    user_type: '1',
  });

  const [errors, setErrors] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
  });

  const { email, password, first_name, last_name } = values;
  const Navigate = useNavigate();

  const handleChange = (e) => {
    let { name, value } = e.target;
    if (name === 'first_name' || name === 'last_name') {
      // Remove spaces
      value = value.replace(/\s/g, '');
      // Limit to 20 characters
      if (value.length > 20) value = value.slice(0, 20);
    }

    setValues({ ...values, [name]: value });
    setErrors({ ...errors, [name]: '' });
  };

  const handleClick = async () => {
    setLoader(true);
    const tempErrors = { ...errors };
    Object.keys(values).forEach((key) => {
      if (!values[key]) {
        tempErrors[key] = ValidationErrors.empty[key];
      } else {
        if (key === 'first_name' && (!validName(values[key]) || values[key].length > 20)) {
          tempErrors.first_name = ValidationErrors.invalid.first_name;
        }
        if (key === 'last_name' && (!validName(values[key]) || values[key].length > 20)) {
          tempErrors.last_name = ValidationErrors.invalid.last_name;
        }
        if (key === 'email' && !validateEmail(values[key])) {
          tempErrors.email = ValidationErrors.invalid.email;
        }
        if (key === 'password' && !validatePwd(values[key])) {
          tempErrors.password = ValidationErrors.invalid.password;
        }
      }
    });
    setErrors(tempErrors);

    if (Object.values(tempErrors).filter((obj) => !!obj).length > 0) {
      setLoader(false);
      return false;
    }

    if (!captchaValue) {
      setLoader(false);
      showToast('Please complete the CAPTCHA', 'error');
      return;
    }

    const payload = {
      ...values,
      ip_address: await getIpAddress(),
    };

    postWithoutToken('user/', payload)
      .then((response) => {
        if (response.status === 201) {
          showToast(response.message, 'success');
          setLocalStorage('userEmail', response?.data?.user?.email);
          Navigate(SESSION_PATHS.OTP_VERIFICATION('register'));
        } else if (response.status === 400) {
          showToast(response.message, 'error');
        } else {
          showToast(response.message, 'error');
        }
      })
      .catch(() => {
        toast.error('Something went wrong');
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const handleGoogleAuth = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        const userInfo = await axios
          .get('https://www.googleapis.com/oauth2/v3/userinfo', {
            headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
          })
          .then((res) => res.data);
        const payload = {
          access_token: tokenResponse.access_token,
          email: userInfo.email,
          first_name: userInfo?.family_name,
          last_name: userInfo?.given_name,
          name: userInfo.name,
          googleId: userInfo.sub,
          ip_address: await getIpAddress(),
        };
        const response = await postWithoutToken('auth/google', payload);
        if (response?.status === 200) {
          setLocalStorage('sidebarToggle', 'false');
          setLocalStorage('apiToken', response?.data?.access);
          setLocalStorage('FullName', response?.data?.first_name + ' ' + response?.data?.last_name);
          setLocalStorage('userDetails', response?.data);

          const subs_id = response?.data?.plan?.subs_id;
          const subscription = response?.data?.plan?.subscription;

          if (subs_id === null) {
            Navigate(PATHS.PRICING, { replace: true });
          } else if (subscription !== null) {
            Navigate(PATHS.DASHBOARD, { replace: true });
          } else {
            Navigate(PATHS.CHATBOT, { replace: true });
          }
          window.location.reload();
          toast.success(response.message || 'Google sign-up successful.', { variant: 'success' });
        } else {
          toast.error(response.message || 'Invalid email or password', { variant: 'error' });
        }
      } catch (error) {
        console.error('Google login process failed:', error);
        toast.error(error.message || 'An error occurred during Google login.', { variant: 'error' });
      }
    },
  });

  const handleMicrosoftAuth = async () => {
    if (inProgress !== InteractionStatus.None) {
      return;
    }

    try {
      const userInfo = await instance.loginPopup(loginRequest);

      const decoded = jwtDecode(userInfo?.accessToken);

      const payload = {
        access_token: userInfo.account.idToken,
        email: userInfo.account.username,
        first_name: decoded.given_name,
        last_name: decoded.family_name,
        name: userInfo.account.name,
        microsoftId: userInfo.account?.idTokenClaims?.sub,
        ip_address: await getIpAddress(),
      };

      // Calling the `loginWithMicrosoft` function
      const response = await postWithoutToken('auth/microsoft', payload);

      if (response?.status === 200) {
        setLocalStorage('sidebarToggle', 'false');
        setLocalStorage('apiToken', response?.data?.access);
        setLocalStorage('FullName', response?.data?.first_name + ' ' + response?.data?.last_name);
        setLocalStorage('userDetails', response?.data);

        const subs_id = response?.data?.plan?.subs_id;
        const subscription = response?.data?.plan?.subscription;

        if (subs_id === null) {
          Navigate(PATHS.PRICING, { replace: true });
        } else if (subscription !== null) {
          Navigate(PATHS.DASHBOARD, { replace: true });
        } else {
          Navigate(PATHS.CHATBOT, { replace: true });
        }
        toast.success(response.message || 'Microsoft sign-up successful.');
        window.location.reload();
      } else {
        toast.error(response.message || 'Registration failed. Please try again.');
      }
    } catch (err) {
      console.error('Microsoft registration failed:', err);
    }
  };

  const onFormSubmit = (e) => {
    e.preventDefault();
  };

  return (
    <AuthLayout title='Sign Up' subtitle='Already have an account?' linkText='Sign in' linkTo={SESSION_PATHS.LOGIN} LeftImage={MOCKUP} Logo={LOGO}>
      <form onSubmit={onFormSubmit}>
        <div class='social-buttons'>
          <button type='button' className='btn-social btn-google flex-fill d-flex align-items-center justify-content-center gap-2' onClick={() => handleGoogleAuth()}>
            {/* <FcGoogle size={20} /> */}
            <svg width='20' height='20' viewBox='0 0 24 24'>
              <path fill='#4285F4' d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z' />
              <path fill='#34A853' d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z' />
              <path fill='#FBBC05' d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z' />
              <path fill='#EA4335' d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z' />
            </svg>
            <span className='outfit-medium'>Google</span>
          </button>
          <button type='button' className='btn-social btn-microsoft flex-fill d-flex align-items-center justify-content-center gap-2' onClick={() => handleMicrosoftAuth()}>
            <svg width='20' height='20' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'>
              <rect x='0' y='0' width='11.377' height='11.372' fill='#F35325' />
              <rect x='12.623' y='0' width='11.377' height='11.372' fill='#81BC06' />
              <rect x='0' y='12.623' width='11.377' height='11.377' fill='#05A6F0' />
              <rect x='12.623' y='12.623' width='11.377' height='11.377' fill='#FFBA08' />
            </svg>
            <span className='outfit-medium'>Microsoft</span>
          </button>
        </div>
        {/* Divider */}
        <div class='auth-divider'>
          <div class='divider-line'></div>
          <span class='divider-text'>Or sign up with</span>
          <div class='divider-line'></div>
        </div>
        <div className='row'>
          <div className='form-group position-relative input-GPT'>
            <label className='auth-label outfit-semibold'>First Name*</label>
            <div className='input-with-icon'>
              <BsPersonFill className='input-icon' />
              <input type='text' name='first_name' value={first_name} placeholder='First Name' className='form-control auth-input' onChange={handleChange} autoComplete='off' />
            </div>
            {errors.first_name && <p className='text-danger insta-smart-error'>{errors.first_name}</p>}
          </div>
          <div className='form-group position-relative input-GPT'>
            <label className='auth-label outfit-semibold'>Last Name*</label>
            <div className='input-with-icon'>
              <BsPersonFill className='input-icon' />
              <input type='text' name='last_name' value={last_name} placeholder='Last Name' className='form-control auth-input' onChange={handleChange} autoComplete='off' />
            </div>
            {errors.last_name && <p className='text-danger insta-smart-error'>{errors.last_name}</p>}
          </div>
        </div>

        <div className='form-group position-relative input-GPT'>
          <label htmlFor='email' className='auth-label outfit-semibold'>
            Email*
          </label>
          <div className='input-with-icon'>
            <MdEmail className='input-icon' />
            <input type='text' className='form-control auth-input' id='email' name='email' value={email} placeholder='Email' autoComplete='off' onChange={handleChange} />
          </div>
          {errors.email && <p className='text-danger insta-smart-error'>{errors.email}</p>}
        </div>

        <div className='form-group position-relative input-GPT'>
          <label className='auth-label outfit-semibold'>Password*</label>
          <div className='input-with-icon'>
            <RiLockPasswordFill className='input-icon' />
            <input
              type={showPwd ? 'text' : 'password'}
              placeholder='Password'
              className='form-control auth-input'
              name='password'
              value={password}
              autoComplete='off'
              onChange={handleChange}
            />
            <button type='button' onClick={() => setShowPwd(!showPwd)} className='password-toggle-btn'>
              {showPwd ? <BsFillEyeFill /> : <BsFillEyeSlashFill />}
            </button>
          </div>
          {errors.password && <p className='text-danger insta-smart-error'>{errors.password}</p>}
        </div>
        <div className='d-flex align-items-center justify-content-center my-3'>
          <ReCAPTCHA ref={recaptchaRef} sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY} badge='inline' data-size='invisible' onChange={(value) => setCaptchaValue(value)} />
        </div>
        <div className='d-flex align-items-center justify-content-between mt-3'>
          <button className='btn-submit-login outfit-bold max-width-100 d-flex align-items-center justify-content-center gap-2' onClick={handleClick}>
            {loader && <ButtonLoader />}
            <span className='outfit-bold'>Create your free Account</span>
          </button>
        </div>
        <div className='text-center mt-3'>
          <p className='terms-text outfit-regular'>
            By continuing, you agree to our{' '}
            <a href='https://www.comniqai.com/terms-of-service' target='_blank' rel='noopener noreferrer' className='terms-link outfit-medium'>
              Terms of Service
            </a>{' '}
            and{' '}
            <a href='https://www.comniqai.com/privacy-policy' target='_blank' rel='noopener noreferrer' className='terms-link outfit-medium'>
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </form>
    </AuthLayout>
  );
};

export default RegisterForm;
